import React, { useEffect, useMemo, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Controller, useForm, useWatch } from 'react-hook-form'
import moment from 'moment/moment'
import { CommonActions, RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { compact } from 'lodash'
import { StyleService, useStyleSheet } from '@src/style/service'
import { NavigationContainer } from '@src/screens/Common/containers'
import { Button, Text } from '@components/base'
import {
  EhrAppointmentProviderKind,
  EhrAppointmentRecurrenceCadence,
  EhrAppointmentRecurrenceDay,
  PotentiallyRecurringSlots,
} from '@src/types.ts'
import { CommonSelect, Feature, InlineAlert, useFeatureFlag } from '@components'
import { MultiSelect } from '@components/MultiSelect.tsx'
import {
  INDEFINITELY_RECURRING_CALLS,
  MAX_RECURRENCE_CALLS,
  orderedCadenceOptions,
  TIME_SLOT_FORMAT,
  UNLIMITED_INSURANCE_CALLS,
  UPSERT_ERROR_MESSAGE,
} from '@screens/NutritionistHub/components/UpcomingCalls/constants.ts'
import {
  toStartCaseOption,
  toStringOption,
  toTimeSlotOption,
} from '@screens/NutritionistHub/components/UpcomingCalls/helpers.ts'
import { AppStackParamList } from '@navigation/types'
import { CustomEventTypes, ErrorMessages } from '@config'
import { useSnack } from '@utils'
import { useNutritionistHubTracker } from '@screens/NutritionistHub/hooks.ts'
import { userSelector } from '@src/selectors/app'
import {
  ehrAppointmentSchedulingInfoSelector,
  topPriorityCoachAssignmentSelector,
} from '../../models/nutritionistHub.selectors'
import { getSchedulingInfoForCoachingKind } from '../NextCall/useCallSchedulingState'
import { useDayTimeOptions } from './useDayTimeOptions'

export const UpsertRecurrence = () => {
  const styles = useStyleSheet(themedStyles)
  const insets = useSafeAreaInsets()
  const dispatch = useDispatch()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const showSnack = useSnack()
  const track = useNutritionistHubTracker()

  const route = useRoute<RouteProp<AppStackParamList, 'UpsertRecurrence'>>()
  const { appointmentRecurrence, insurancePolicy, proposed } = route.params

  const recurringAppointmentsSchedulingEnabled = useFeatureFlag(
    Feature.RecurringAppointmentsScheduling,
  )

  const user = useSelector(userSelector)
  const coachAssignment = useSelector(topPriorityCoachAssignmentSelector)
  const allSchedulingInfo = useSelector(ehrAppointmentSchedulingInfoSelector)
  const primaryCoachingKind =
    user && 'primaryCoachingKind' in user ? user.primaryCoachingKind : undefined

  const [
    potentiallyRecurringSlots,
    setPotentiallyRecurringSlots,
  ] = useState<PotentiallyRecurringSlots | null>(null)
  const [upsertLoading, setUpsertLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const coachingSchedulingInfo = useMemo(() => {
    const coachingKind = primaryCoachingKind?.key

    if (!coachingKind) {
      return undefined
    }

    return getSchedulingInfoForCoachingKind(allSchedulingInfo, coachingKind)
  }, [allSchedulingInfo, primaryCoachingKind])

  const appointmentKind = coachingSchedulingInfo?.options.find((o) => o.recurring)?.appointmentKind
  const upsertButtonText = appointmentRecurrence ? 'Save' : 'Create a Recurring Call'
  const hasUnlimitedCall = insurancePolicy?.unitsLeft === UNLIMITED_INSURANCE_CALLS
  const maxCalls = () => {
    if (insurancePolicy?.eligible) {
      return hasUnlimitedCall ? MAX_RECURRENCE_CALLS : insurancePolicy.unitsLeft || 0
    }

    return MAX_RECURRENCE_CALLS
  }
  const numberOfCallOptions = [
    recurringAppointmentsSchedulingEnabled ? toStringOption(INDEFINITELY_RECURRING_CALLS) : null,
    ...Array.from({ length: maxCalls() }, (_, i) => i + 1).map(toStringOption),
  ].filter(Boolean) as ReturnType<typeof toStringOption>[]

  const defaultValues = {
    cadence: appointmentRecurrence?.cadence
      ? toStartCaseOption(appointmentRecurrence.cadence)
      : toStartCaseOption(EhrAppointmentRecurrenceCadence.Weekly),
    days: (appointmentRecurrence?.days || []).map(toStartCaseOption),
    timeSlots: (appointmentRecurrence?.timeSlots || []).map(toTimeSlotOption),
    numberOfCalls: appointmentRecurrence?.numberOfCalls
      ? toStringOption(appointmentRecurrence.numberOfCalls)
      : toStringOption(INDEFINITELY_RECURRING_CALLS),
  }
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm({
    defaultValues,
  })
  const days = useWatch({ control, name: 'days' })
  const cadence = useWatch({ control, name: 'cadence' })

  const { dayOptions, timeSlotOptions } = useDayTimeOptions({
    availabilities: potentiallyRecurringSlots,
    cadence: cadence.value,
    days: days.map((day) => day.value) as EhrAppointmentRecurrenceDay[],
  })

  useEffect(() => {
    if (!coachAssignment?.coach || !coachingSchedulingInfo || !appointmentKind) {
      return
    }

    dispatch({
      type: 'nutritionistHub/fetchPotentiallyRecurringSlots',
      payload: {
        appointmentKind,
        providerKind: EhrAppointmentProviderKind.Coach,
        providerId: coachAssignment.coach.id,
      },
      success: (response: PotentiallyRecurringSlots) => {
        setPotentiallyRecurringSlots(response)
        if (
          response.weekly.length === 0 &&
          response.biWeekly.length === 0 &&
          response.monthly.length === 0
        ) {
          showSnack(
            'No available days found for recurring calls. Please contact support.',
            null,
            'warning',
          )
          track(CustomEventTypes.AppointmentRecurrenceEmptyAvailability, {
            usePotentiallyRecurringSlots: true,
          })
        }
      },
    })
  }, [dispatch, coachAssignment, track, showSnack, appointmentKind, coachingSchedulingInfo])

  const onUpsertRecurrence = handleSubmit(({ cadence, days, numberOfCalls, timeSlots }) => {
    if (
      dayOptions.filter((day) => day.value !== EhrAppointmentRecurrenceDay.All).length === 0 ||
      timeSlotOptions.length === 0
    ) {
      return
    }

    const recurrence = {
      cadence: cadence.value as EhrAppointmentRecurrenceCadence,
      days: days.map((day) => day.value as EhrAppointmentRecurrenceDay),
      numberOfCalls: numberOfCalls.value as number,
      timeSlots: timeSlots.map((timeSlot) => moment(timeSlot.value, TIME_SLOT_FORMAT)),
      appointmentKind,
    }

    track(
      appointmentRecurrence
        ? CustomEventTypes.NutritionistHubRecurrenceUpdated
        : CustomEventTypes.NutritionistHubRecurrenceCreated,
    )

    setUpsertLoading(true)
    dispatch({
      type: 'nutritionistHub/upsertAppointmentRecurrence',
      payload: recurrence,
      success: () => {
        dispatch({ type: 'nutritionistHub/fetchAppointmentRecurrence' })
        navigation.dispatch((state) => {
          // Remove current route
          const routes = state.routes.slice(0, -1)

          // navigate to NutritionistHub with UpsertRecurrenceSuccessfulModal
          const newRoutes = [
            ...routes,
            {
              name: 'Drawer',
              params: { screen: 'Dashboard', params: { screen: 'NutritionistHub' } },
            },
            { name: 'UpsertRecurrenceSuccessfulModal' },
          ]

          return CommonActions.reset({
            index: newRoutes.length - 1,
            routes: newRoutes,
          } as any)
        })
      },
      failure: (error: any) => {
        const errorMessage = error?.message || UPSERT_ERROR_MESSAGE
        showSnack(errorMessage, null, 'error')
      },
      complete: () => {
        setUpsertLoading(false)
      },
    })
  })

  const onDeleteRecurrence = () => {
    track(CustomEventTypes.NutritionistHubRecurrenceDeleted)
    setDeleteLoading(true)
    dispatch({
      type: 'nutritionistHub/deleteAppointmentRecurrence',
      success: () => {
        dispatch({ type: 'nutritionistHub/fetchAppointmentRecurrence' })
        dispatch({ type: 'nutritionistHub/fetchUpcomingEhrAppointments' })

        navigation.navigate('AllVideoCalls')
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
      },
      complete: () => {
        setDeleteLoading(false)
      },
    })
  }

  const title = () => {
    if (proposed) {
      return 'Create Recurrence'
    }

    return appointmentRecurrence ? 'Edit Recurrence' : 'Create Recurrence'
  }

  const handleSkipNow = () => {
    navigation.navigate('Drawer', { screen: 'Dashboard', params: { screen: 'NutritionistHub' } })
  }

  return (
    <NavigationContainer title={title()} style={{ flex: 1 }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[styles.container, { paddingBottom: insets.bottom }]}
      >
        <View style={styles.form}>
          <Controller
            control={control}
            name="cadence"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <CommonSelect
                options={orderedCadenceOptions.map(toStartCaseOption)}
                selectedOption={value}
                label="How frequently would you like to have your calls?"
                placeholder="Cadence"
                onSelect={onChange}
                style={{ marginBottom: 24 }}
              />
            )}
          />
          <Controller
            control={control}
            name="days"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <View style={{ marginBottom: 24 }}>
                <MultiSelect
                  options={dayOptions}
                  selectedOptions={value}
                  label="What are your preferred day(s) of the week?"
                  placeholder="Days"
                  // compacting since previously selected day might not be available anymore
                  onSelect={(option) => onChange(compact(option))}
                />
                <Text type="regular" style={styles.caption}>
                  Select all that apply.
                </Text>
              </View>
            )}
          />
          <Controller
            control={control}
            name="timeSlots"
            render={({ field: { value, onChange } }) => (
              <View style={{ marginBottom: 24 }}>
                <MultiSelect
                  options={timeSlotOptions}
                  selectedOptions={value}
                  label="What are your preferred time slots?"
                  placeholder="Time Slots"
                  // compacting since previously selected slot might not be available anymore
                  onSelect={(option) => onChange(compact(option))}
                />
                <Text type="regular" style={styles.caption}>
                  Select all that apply.
                </Text>
              </View>
            )}
          />
          <Controller
            control={control}
            name="numberOfCalls"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <CommonSelect
                options={numberOfCallOptions}
                selectedOption={value}
                label="How many calls would you like to schedule?"
                placeholder="Number of Calls"
                onSelect={onChange}
                style={{ marginBottom: 24 }}
              />
            )}
          />
        </View>
        <View style={styles.bottomContainer}>
          <InlineAlert
            style={[styles.bottomItem, { marginBottom: 4 }]}
            message={
              appointmentRecurrence && !proposed
                ? 'Editing a recurrence will delete all your scheduled recurring calls ' +
                  'and replace them according to your new preferences.'
                : 'Your recurrence may take a few minutes to complete once submitted.'
            }
          />
          <Button
            type="primary"
            size="block"
            disabled={!isValid || upsertLoading}
            style={styles.bottomItem}
            onPress={onUpsertRecurrence}
            accessibilityLabel={upsertButtonText}
          >
            {upsertButtonText}
          </Button>
          {appointmentRecurrence && !proposed && (
            <Button
              type="transparent"
              size="block"
              disabled={deleteLoading}
              style={styles.bottomItem}
              textStyle={styles.deleteButtonText}
              onPress={onDeleteRecurrence}
              accessibilityLabel="Delete Recurrence"
            >
              Delete Recurrence
            </Button>
          )}
          {proposed && (
            <Button
              type="transparent"
              size="block"
              style={styles.bottomItem}
              onPress={handleSkipNow}
              accessibilityLabel="Skip for Now"
            >
              Skip for Now
            </Button>
          )}
        </View>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flexGrow: 1,
    backgroundColor: 'theme.background',
    padding: 16,
  },
  form: {
    flex: 1,
  },
  bottomContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  caption: {
    marginTop: 4,
    color: 'theme.text.secondary',
  },
  bottomItem: {
    marginTop: 'auto',
  },
  deleteButtonText: {
    color: 'theme.error.base',
  },
})
